import React, { useCallback, useEffect, useRef, useMemo, useState } from "react";
import {
  ArrowRight,
  Bell,
  Building,
  CalendarClock,
  Check,
  CheckCircle,
  ChevronDown,
  FileText,
  Filter,
  Folders,
  History,
  LayoutDashboard,
  Loader2,
  Lock,
  Mail,
  Play,
  ShieldCheck,
  Sparkles,
  Star,
  Tag,
  UserCog,
  Users,
  Paperclip,
  ClipboardList,
  Zap
} from "lucide-react";
import SEO from "../components/SEO";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import MarketingTestimonials from "../components/MarketingTestimonials";
import MarketingComparisonTable from "../components/MarketingComparisonTable";
import MarketingPricingCards from "../components/MarketingPricingCards";
import MarketingFAQ from "../components/MarketingFAQ";
import { marketingNavLinks } from "../constants/marketingNav";

const features = [
  {
    title: "Kanban Boards",
    description:
      "Visualize your workflow with intuitive drag-and-drop Kanban boards. Move tasks seamlessly across customizable columns.",
    icon: LayoutDashboard,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
  {
    title: "Real-Time Collaboration",
    description:
      "Work together in real-time with instant updates. See changes as they happen with WebSocket-powered live sync.",
    icon: Users,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
  {
    title: "Secure Credentials",
    description:
      "Store project credentials safely with role-based access control. API keys, hosting details, and secrets in one secure place.",
    icon: ShieldCheck,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
  {
    title: "Smart Notifications",
    description:
      "Never miss an update with intelligent notifications. Get alerts for assignments, mentions, due dates, and more.",
    icon: Bell,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
  {
    title: "Team Management",
    description:
      "Manage roles, permissions, and invitations with ease. Admin controls give you full visibility over your team.",
    icon: UserCog,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
  {
    title: "Advanced Filtering",
    description:
      "Find anything instantly with powerful search and filters. Filter by assignee, due date, labels, status, and more.",
    icon: Filter,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
  {
    title: "Project Categories",
    description:
      "Organize projects into categories for better management. Group by client, department, or any structure that works for you.",
    icon: Folders,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
  {
    title: "File Attachments",
    description:
      "Attach files, images, and documents directly to tasks. Keep all project assets organized in one place.",
    icon: Paperclip,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
  {
    title: "Due Dates & Deadlines",
    description: "Set due dates for tasks and never miss a deadline. Get automatic reminders before tasks are due.",
    icon: CalendarClock,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
  {
    title: "Activity History",
    description:
      "Track every change with detailed activity logs. See who did what and when for complete transparency.",
    icon: History,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
  {
    title: "Priority Labels",
    description:
      "Mark tasks as urgent, high, medium, or low priority. Visual indicators help teams focus on what matters most.",
    icon: Tag,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
  {
    title: "Email Invitations",
    description:
      "Invite team members via email with one click. New users can join projects instantly without complicated onboarding.",
    icon: Mail,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
];

const howItWorks = [
  { step: 1, title: "Create Your Project", description: "Set up your project in seconds. Define columns, add descriptions, and configure your workflow." },
  { step: 2, title: "Invite Your Team", description: "Send email invitations to team members. Assign roles and permissions with a single click." },
  { step: 3, title: "Track & Deliver", description: "Manage tasks with Kanban boards, track progress in real-time, and deliver projects successfully." },
];

const showcases = [
  {
    id: "workflow",
    eyebrow: "Workflow Management",
    title: "Intuitive Kanban Boards",
    description:
      "Visualize your entire project workflow with beautiful, drag-and-drop Kanban boards. Customize columns to match your process and move tasks effortlessly.",
    bullets: ["Drag-and-drop task management", "Customizable columns and workflows", "Priority labels and due dates"],
    visual: "kanban",
    reverse: false,
  },
  {
    id: "security",
    eyebrow: "Security First",
    title: "Secure Credentials Manager",
    description:
      "Keep your project secrets safe with our built-in credentials manager. Store API keys, hosting details, database credentials, and more with granular access control.",
    bullets: ["Role-based access control", "One-click copy to clipboard", "Audit trail for access history"],
    visual: "vault",
    reverse: true,
  },
  {
    id: "collaboration",
    eyebrow: "Collaboration",
    title: "Seamless Team Collaboration",
    description:
      "Work together in real-time with @mentions, comments, and activity tracking. Keep everyone in sync with instant notifications and live updates.",
    bullets: ["@mention team members in comments", "Real-time activity feed", "Email-based team invitations"],
    visual: "collaboration",
    reverse: false,
  },
];

const trustedLogos = ["TechCorp", "StartupX", "DesignLab", "CloudBase", "AgencyPro"];
const contactHighlights = ["Free personalized demo", "No commitment required", "Custom pricing for your team"];

const ShowcaseVisual = ({ type }) => {
  if (type === "kanban") {
    return (
      <div className="bg-white rounded-2xl shadow-[0_20px_50px_rgba(34,40,117,0.12)] p-6 border border-gray-100">
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="text-xs font-semibold text-blue-700 mb-2">To Do</div>
            <div className="space-y-2">
              <div className="bg-white rounded p-2 shadow-sm border border-blue-100">
                <div className="w-full h-2 bg-gray-200 rounded mb-2" />
                <div className="w-2/3 h-2 bg-gray-100 rounded" />
              </div>
              <div className="bg-white rounded p-2 shadow-sm border border-blue-100">
                <div className="w-full h-2 bg-gray-200 rounded mb-2" />
                <div className="w-1/2 h-2 bg-gray-100 rounded" />
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3">
            <div className="text-xs font-semibold text-yellow-700 mb-2">In Progress</div>
            <div className="space-y-2">
              <div className="bg-white rounded p-2 shadow-sm border border-yellow-100 ring-2 ring-primary-500">
                <div className="w-full h-2 bg-primary-200 rounded mb-2" />
                <div className="w-3/4 h-2 bg-gray-100 rounded" />
              </div>
            </div>
          </div>
          <div className="bg-purple-50 rounded-lg p-3">
            <div className="text-xs font-semibold text-purple-700 mb-2">Review</div>
            <div className="space-y-2">
              <div className="bg-white rounded p-2 shadow-sm border border-purple-100">
                <div className="w-full h-2 bg-gray-200 rounded mb-2" />
                <div className="w-2/3 h-2 bg-gray-100 rounded" />
              </div>
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <div className="text-xs font-semibold text-green-700 mb-2">Done</div>
            <div className="space-y-2">
              <div className="bg-white rounded p-2 shadow-sm border border-green-100">
                <div className="w-full h-2 bg-green-200 rounded mb-2" />
                <div className="w-1/2 h-2 bg-gray-100 rounded" />
              </div>
              <div className="bg-white rounded p-2 shadow-sm border border-green-100">
                <div className="w-full h-2 bg-green-200 rounded mb-2" />
                <div className="w-2/3 h-2 bg-gray-100 rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (type === "vault") {
    return (
      <div className="bg-white rounded-2xl shadow-[0_20px_50px_rgba(34,40,117,0.12)] p-6 border border-gray-100">
        <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-100">
          <ShieldCheck className="w-5 h-5 text-primary-600" />
          <span className="font-semibold text-gray-800">Project Credentials</span>
        </div>
        {[
          { title: "Domain Access", subtitle: "example.com" },
          { title: "Hosting (FTP)", subtitle: "ftp.example.com" },
          { title: "API Keys", subtitle: "sk_live_••••••••" },
        ].map((item) => (
          <div
            key={item.title}
            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors mb-3 last:mb-0"
          >
            <div>
              <div className="text-sm font-medium text-gray-800">{item.title}</div>
              <div className="text-xs text-gray-500">{item.subtitle}</div>
            </div>
            <ClipboardList className="w-5 h-5 text-primary-600" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-[0_20px_50px_rgba(34,40,117,0.12)] p-6 border border-gray-100">
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold">J</div>
          <div className="flex-1 bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-gray-800 text-sm">John</span>
              <span className="text-xs text-gray-500">2 min ago</span>
            </div>
            <p className="text-sm text-gray-600">
              Hey <span className="text-primary-600 font-medium">@Sarah</span>, can you review the new design?
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">S</div>
          <div className="flex-1 bg-primary-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-gray-800 text-sm">Sarah</span>
              <span className="text-xs text-gray-500">Just now</span>
            </div>
            <p className="text-sm text-gray-600">On it! Moving to review column now.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Check className="w-4 h-4 text-green-500" />
          <span>Sarah moved "Homepage Design" to Review</span>
        </div>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const [backToTopVisible, setBackToTopVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [active, setActive] = useState(false);
  const ref = useRef(null);

  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Testimonials") return { ...link, route: "/testimonials" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  const handleScrollState = useCallback(() => {
    const y = window.scrollY;
    setBackToTopVisible(y > 500);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScrollState, { passive: true });
    handleScrollState();
    return () => window.removeEventListener("scroll", handleScrollState);
  }, [handleScrollState]);

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .reveal, .landing-page .reveal-left, .landing-page .reveal-right, .landing-page .section-animate"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const smoothScrollTo = useCallback((id) => {
    const target = document.getElementById(id);
    if (!target) return;
    const headerOffset = 80;
    const offset = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offset, behavior: "smooth" });
  }, []);

  const handleNavClick = (event, target) => {
    event.preventDefault();
    smoothScrollTo(target);
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    setShowModal(true);
    event.target.reset();
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setActive(entry.isIntersecting),
      { threshold: 0.4 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white">
      <SEO 
        title="Intelligent Kanban & Project Management"
        description="Streamline your workflow with TeamBoard. Features Gemini AI task extraction, secure credentials vault, and seamless Salesforce integration."
        keywords="project management, kanban board, gemini ai, leanport software, secure pms, agile tools"
      />
      <MarketingHeader navLinks={navLinks} onNavClick={handleNavClick} ctaTarget="contact" />

      <main>
        <section id="hero" className="curved-section shell-sky section-animate relative min-h-screen flex items-center overflow-hidden pt-16 pb-20">
          <div className="absolute -top-20 right-[-100px] w-80 h-80 rounded-[90px] " />
          <div className="absolute -top-20 right-[-100px] w-80 h-80 rounded-[90px] bg-blue-100/50" />
          <div className="absolute -bottom-24 -left-48 w-96 h-96 rounded-[140px] bg-[#222875]/5" />

          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8 py-12 z-10 absolute -left-96">
            <div className="grid lg:grid-cols-2 gap-48 items-center">
              <div className="text-center lg:text-left space-y-8">
                <a
                  href="#features"
                  onClick={(e) => handleNavClick(e, "features")}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-full transition-all group reveal"
                >
                  <span className="flex h-2 w-2 relative">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                  </span>
                  <span className="text-blue-700 text-sm font-medium">New: Secure Credentials Vault</span>
                  <ArrowRight className="w-3.5 h-3.5 text-blue-600 group-hover:translate-x-1 transition-transform" />
                </a>

                <h1 className="reveal stagger-1">
                  <span className="text-gray-900">Work Smarter,</span>
                  <span className="block text-blue-600 mt-2">Ship Faster</span>
                </h1>

                <p className="text-lead reveal stagger-2 mx-auto lg:mx-0 max-w-2xl">
                  The all-in-one workspace where teams plan, track, and deliver projects that matter.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start reveal stagger-3">
                  <a
                    href="#contact"
                    onClick={(e) => handleNavClick(e, "contact")}
                    className="btn-primary"
                  >
                    Get Started Free
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </a>
                  <a
                    href="#showcase"
                    onClick={(e) => handleNavClick(e, "showcase")}
                    className="btn-secondary"
                  >
                    <Play className="w-5 h-5 mr-2 text-navy" />
                    See It in Action
                  </a>
                </div>

                <div className="space-y-6 reveal stagger-4">
                  <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                    <span className="text-sm text-gray-600 font-medium">Trusted by teams at:</span>
                    <div className="flex items-center gap-6 opacity-50">
                      <div className="flex items-center gap-2 text-gray-700 font-bold text-lg">
                        <Sparkles className="w-5 h-5" />
                        <span>Acme Co</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700 font-bold text-lg">
                        <Building className="w-5 h-5" />
                        <span>TechCorp</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 justify-center lg:justify-start">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                      ))}
                    </div>
                    <span className="text-gray-700 font-bold">4.9</span>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-600 font-medium">2,000+ reviews</span>
                  </div>
                </div>
              </div>

              <div className="relative reveal stagger-4 lg:scale-110">
                <div className="relative rounded-[40px] bg-white p-6 md:p-10 border border-gray-100 shadow-[0_25px_60px_rgba(34,40,117,0.18)] overflow-hidden">
                  <div className="relative bg-white rounded-2xl shadow-[0_20px_50px_rgba(34,40,117,0.14)] p-8 border border-gray-100">
                    <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100">
                      <div className="w-3 h-3 rounded-full bg-[#f87171]" />
                      <div className="w-3 h-3 rounded-full bg-[#facc15]" />
                      <div className="w-3 h-3 rounded-full bg-[#4ade80]" />
                      <div className="flex-1 mx-4">
                        <div className="bg-gray-100 rounded-lg h-9 flex items-center px-4">
                          <Lock className="w-3.5 h-3.5 text-gray-400 mr-2" />
                          <span className="text-gray-500 text-sm font-medium">pms.example.com</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-bold text-gray-800">To Do</span>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-semibold">3</span>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 shadow-sm">
                          <div className="text-sm font-semibold text-gray-800 mb-3">Design homepage</div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-md font-medium">High</span>
                            <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">J</div>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-bold text-gray-800">In Progress</span>
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-semibold">2</span>
                        </div>
                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100 shadow-sm">
                          <div className="text-sm font-semibold text-gray-800 mb-3">User auth</div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-md font-medium">Urgent</span>
                            <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center text-white text-xs font-bold">A</div>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-bold text-gray-800">Done</span>
                          <span className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-semibold">5</span>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg border border-green-100 shadow-sm">
                          <div className="flex items-center gap-2 mb-3">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <div className="text-sm font-semibold text-gray-800">Database</div>
                          </div>
                          <div className="w-7 h-7 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold ml-auto">S</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="absolute -top-6 -right-6 bg-white rounded-lg shadow-2xl p-4 border border-gray-100 animate-bounce-gentle z-10">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-gray-900">Task Completed!</div>
                      <div className="text-xs text-gray-600">Sarah finished "Setup"</div>
                    </div>
                  </div>
                </div>
                {/* Floating Team Members - Online */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-2xl p-4 border border-gray-100 animate-float z-10">
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-2">
                      {[
                        { initial: "J", color: "bg-blue-600" },
                        { initial: "M", color: "bg-purple-500" },
                        { initial: "A", color: "bg-green-500" },
                      ].map((member, idx) => (
                        <div
                          key={idx}
                          className={`w-9 h-9 rounded-full ${member.color} text-white text-sm font-bold border-2 border-white shadow flex items-center justify-center`}
                        >
                          {member.initial}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-sm text-gray-700 font-semibold">5 online now</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-6 h-6 text-gray-400" />
          </div>
        </section>

        <section id="trusted-by" className="shell-white section-narrow border-y border-gray-50">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-gray-400 text-sm font-bold uppercase tracking-widest mb-10 reveal">Trusted by teams at</p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-40 grayscale reveal stagger-1">
              {trustedLogos.map((logo) => (
                <div key={logo} className="flex items-center gap-2 text-2xl font-black text-gray-800">
                  <Sparkles className="w-8 h-8" />
                  {logo}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="shell-white">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="relative reveal">
                <div className="relative bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
                  <div className="flex items-center gap-3 mb-8 pb-4 border-b border-gray-100">
                    <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center">
                      <Mail className="w-6 h-6 text-sky" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Incoming Tasks</h3>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 shadow-sm">
                      <div className="text-sm font-bold text-gray-900 mb-1">Prepare Q4 marketing analysis</div>
                      <div className="text-xs text-gray-500">From: Client X</div>
                    </div>
                    <div className="bg-green-50 border border-green-100 rounded-2xl p-5 shadow-sm">
                      <div className="text-sm font-bold text-gray-900 mb-1">Website redesign feedback</div>
                      <div className="text-xs text-gray-500">From: Design Team</div>
                    </div>
                  </div>
                </div>
                <div className="absolute -right-10 top-1/4 space-y-4 hidden lg:block">
                  <div className="bg-navy text-sky rounded-2xl px-6 py-4 shadow-2xl w-56 transform rotate-2">
                    <div className="text-xs font-bold opacity-80 uppercase tracking-widest mb-1">Productivity</div>
                    <div className="text-lg font-bold">10x Faster Work</div>
                  </div>
                </div>
              </div>

              <div className="reveal stagger-1">
                <span className="section-eyebrow">Organization</span>
                <h2 className="mb-6">Seamlessly organize your <span className="text-blue-600">project workflow</span></h2>
                <p className="text-body mb-8">
                  Stop juggling multiple tools. PMS brings your tasks, credentials, and collaboration into a single, cohesive interface.
                </p>
                <div className="space-y-4">
                  {[
                    "Drag-and-drop task management",
                    "Integrated credentials vault",
                    "Real-time team notifications"
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                        <Check className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-lg font-semibold text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="ai-features" className="shell-navy relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-sky rounded-full blur-3xl -mr-80 -mt-80" />
          </div>
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-20 reveal">
              <span className="section-eyebrow">Gemini AI Powered</span>
              <h2 className="mt-6 mb-6">Work Smarter with <span className="text-sky">Intelligent Automation</span></h2>
              <p className="text-lead text-blue-100 max-w-3xl mx-auto">
                Transform complex documents into actionable tasks in seconds. Our AI-driven extraction handles the heavy lifting.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="reveal-left">
                <div className="bg-white rounded-[40px] p-10 shadow-2xl">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 bg-navy rounded-2xl flex items-center justify-center">
                      <Sparkles className="w-8 h-8 text-sky" />
                    </div>
                    <div>
                      <div className="text-xs font-black text-blue-600 uppercase tracking-widest mb-1">Processing</div>
                      <div className="text-xl font-bold text-gray-900">AI Ticket Extraction</div>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div className="p-6 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl text-center">
                      <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <div className="text-sm font-bold text-gray-600">project-spec.pdf</div>
                      <div className="text-xs text-gray-400 mt-1">Ready for analysis</div>
                    </div>
                    <div className="flex items-center justify-between text-sm font-bold">
                      <span className="text-gray-500">Extracting tickets...</span>
                      <span className="text-blue-600">87%</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full w-[87%] transition-all" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-10 reveal-right">
                {[
                  {
                    title: "Smart Document Analysis",
                    desc: "Gemini AI scans your project briefs and extracts key deliverables automatically."
                  },
                  {
                    title: "Automatic Prioritization",
                    desc: "Tasks are intelligently tagged with priority levels based on document urgency cues."
                  },
                  {
                    title: "Context-Aware Categorization",
                    desc: "AI identifies the best project category and assignee for each extracted task."
                  }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-6">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-6 h-6 text-sky" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-white">{item.title}</h3>
                      <p className="text-blue-100">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="shell-white">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-start mb-20">
              <span className="section-eyebrow reveal">The PMS Stack</span>
              <h2 className="reveal stagger-1 mt-4 mb-6">Everything your team <br /> needs to <span className="text-navy">execute flawlessly</span></h2>
              <p className="text-lead reveal stagger-2">Powerful features designed for modern engineering and design workflows.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {features.map((feature, idx) => (
                <div
                  key={feature.title}
                  className={`card-marketing card-hover reveal stagger-${(idx % 6) + 1}`}
                >
                  <div className={`w-14 h-14 ${feature.color} rounded-2xl flex items-center justify-center mb-6`}>
                    <feature.icon className={`w-7 h-7 ${feature.iconColor}`} />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <MarketingComparisonTable />
        <MarketingTestimonials />
        <MarketingPricingCards />
        <MarketingFAQ />

        <section id="contact" className="shell-sky">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="reveal-left">
                <span className="section-eyebrow">Contact Us</span>
                <h2 className="mb-8">Ready to transform your <span className="text-navy">team's workflow?</span></h2>
                <p className="text-lead mb-10">
                  Join hundreds of high-performing teams already using PMS. Request a personalized demo today.
                </p>
                <div className="space-y-6">
                  {contactHighlights.map((item) => (
                    <div key={item} className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm">
                        <Check className="w-6 h-6 text-green-600" />
                      </div>
                      <span className="text-lg font-bold text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="reveal-right">
                <form
                  onSubmit={handleFormSubmit}
                  className="bg-white rounded-[40px] p-10 shadow-2xl border border-gray-50"
                >
                  <h3 className="text-2xl font-bold mb-8">Schedule Your Demo</h3>
                  <div className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <input
                        required
                        className="w-full px-6 py-4 rounded-lg bg-gray-50 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-navy transition-all"
                        placeholder="Full Name"
                        type="text"
                      />
                      <input
                        required
                        className="w-full px-6 py-4 rounded-lg bg-gray-50 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-navy transition-all"
                        placeholder="Work Email"
                        type="email"
                      />
                    </div>
                    <input
                      className="w-full px-6 py-4 rounded-lg bg-gray-50 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-navy transition-all"
                      placeholder="Company Name"
                      type="text"
                    />
                    <textarea
                      rows="4"
                      className="w-full px-6 py-4 rounded-lg bg-gray-50 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-navy transition-all resize-none"
                      placeholder="Tell us about your needs..."
                    />
                    <button
                      type="submit"
                      className="btn-primary w-full py-5"
                    >
                      Request Demo
                      <ArrowRight className="w-6 h-6 ml-2" />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter onNavClick={handleNavClick} />
      
      <button
        aria-label="Back to top"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={`fixed bottom-8 right-8 w-14 h-14 bg-navy text-sky rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-2xl z-50 ${backToTopVisible ? "opacity-100 visible" : "opacity-0 invisible"}`}
      >
        <ArrowRight className="w-6 h-6 -rotate-90" />
      </button>

      {showModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100]"
          onClick={() => setShowModal(false)}
        >
          <div className="bg-white rounded-[40px] p-12 max-w-md mx-4 text-center animate-scale-in">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-3xl font-black mb-4">Thank You!</h3>
            <p className="text-gray-600 text-lg mb-8">
              We've received your request. Our team will reach out within 24 hours.
            </p>
            <button
              onClick={() => setShowModal(false)}
              className="btn-primary w-full py-4"
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
